import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TEBEX_PUBLIC_TOKEN } from '../config';
import { createBasket, addPackageToBasket } from '../api/tebex';

export function useTebexCheckout() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const clearError = useCallback(() => setError(null), []);

  const buyPackage = useCallback(async (packageId) => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }

    if (!TEBEX_PUBLIC_TOKEN) {
      setError('Store is not configured yet. Please try again later.');
      return;
    }

    if (!packageId) {
      setError('This item is not available for purchase yet.');
      return;
    }

    // Prefer the verified Minecraft username - falls back to account username,
    // which is the MC username for Mojang/Microsoft logins and linked accounts.
    const mcUsername = user.mojang_username || user.username;
    if (!mcUsername) {
      setError('Link your Minecraft account before buying treats.');
      return;
    }

    setIsLoading(true);
    setError(null);

    let launched = false;
    try {
      const basketResponse = await createBasket(mcUsername);
      const basketIdent = basketResponse.data?.ident;
      if (!basketIdent) {
        throw new Error('Failed to create checkout session');
      }

      await addPackageToBasket(basketIdent, packageId);

      const { default: Tebex } = await import('@tebexio/tebex.js');

      Tebex.checkout.init({
        ident: basketIdent,
        theme: 'dark',
        popupOnMobile: true,
      });

      Tebex.checkout.on('payment:complete', () => {
        setCheckoutOpen(false);
        setIsLoading(false);
        navigate('/shop?purchase=success');
      });

      Tebex.checkout.on('payment:error', (evt) => {
        setCheckoutOpen(false);
        setIsLoading(false);
        setError(evt?.detail?.message || 'Payment failed. Please try again.');
      });

      Tebex.checkout.on('close', () => {
        setCheckoutOpen(false);
        setIsLoading(false);
      });

      Tebex.checkout.launch();
      setCheckoutOpen(true);
      launched = true;
    } catch (err) {
      setError(err.message || 'Checkout failed. Please try again.');
    } finally {
      if (!launched) setIsLoading(false);
    }
  }, [isAuthenticated, user, navigate]);

  return { buyPackage, isLoading, error, clearError, checkoutOpen };
}
